import { aiOpponentsService } from './aiOpponentsService';
import { aiStrategy } from './aiStrategy';
import { calculateMoveEffects, applyEffects, isGameOver } from '../utils/battleUtils';
import type { User } from '../types/user';
import type { UserPet } from '../types/pets';
import type { UserItem } from '../types/items';
import type { BattleMove, BattleState, BattleEffect } from '../types/battle';

export class AiBattleService {
  startBattle(user: User, userPets: UserPet[], userItems: UserItem[], wagerAmount: number): BattleState {
    try {
      const opponent = aiOpponentsService.findOpponent(wagerAmount);
      if (!opponent) {
        throw new Error('No suitable AI opponent found');
      }

      // Validate opponent data
      if (!opponent.pets?.length) {
        throw new Error('AI opponent has no pets configured');
      }

      // Ensure AI pets have proper image URLs
      const aiPets = opponent.pets.map(pet => ({
        ...pet,
        id: crypto.randomUUID(),
        level: Math.floor(Math.random() * 10) + 5,
        experience: 0,
        equippedItems: [],
        createdAt: new Date().toISOString()
      }));

      // Ensure AI items have proper data
      const aiItems = (opponent.items || []).map(item => ({
        ...item,
        id: crypto.randomUUID(),
        quantity: 1,
        createdAt: new Date().toISOString()
      }));

      const initialState: BattleState = {
        battleId: crypto.randomUUID(),
        playerHealth: 100,
        aiHealth: 100,
        playerPets: userPets,
        playerItems: userItems,
        aiPets,
        aiItems,
        currentTurn: 'player',
        moves: [],
        activeEffects: {
          player: [],
          ai: []
        },
        specialAttacksUsed: {
          player: 0,
          ai: 0
        },
        wagerAmount,
        startTime: new Date().toISOString(),
        gameOver: false
      };

      return initialState;
    } catch (error) {
      console.error('Failed to start battle:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to initialize battle');
    }
  }

  // Rest of the class implementation remains the same
  processPlayerMove(state: BattleState, playerMove: BattleMove) {
    if (state.gameOver) {
      throw new Error('Game is already over');
    }

    // Calculate and apply player move effects
    const effects = calculateMoveEffects(playerMove, { ...state, currentTurn: 'player' });
    const newState = applyEffects({ ...state, currentTurn: 'player' }, effects, 'ai');

    // Update game state
    newState.currentTurn = 'ai';
    newState.gameOver = isGameOver(newState);
    newState.moves = [...state.moves, { player: playerMove, ai: null! }];

    return { newState, effects };
  }

  processAiMove(state: BattleState) {
    if (state.gameOver) {
      throw new Error('Game is already over');
    }

    // Calculate AI's move based on strategy and state
    const aiMove = aiStrategy.calculateNextMove({ ...state, currentTurn: 'ai' });
    
    // Calculate and apply AI move effects
    const effects = calculateMoveEffects(aiMove, { ...state, currentTurn: 'ai' });
    const newState = applyEffects({ ...state, currentTurn: 'ai' }, effects, 'player');

    // Update game state
    newState.currentTurn = 'player';
    newState.gameOver = isGameOver(newState);
    
    // Update the last move with AI's response
    const lastMove = newState.moves[newState.moves.length - 1];
    newState.moves[newState.moves.length - 1] = {
      ...lastMove,
      ai: aiMove
    };

    return { newState, effects };
  }
}

export const aiBattleService = new AiBattleService();